/* eslint-disable unicorn/prefer-at */
/* eslint-disable no-console */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import cx from "classix";
import { useAuthContext } from "../../context/auth";
import { getCategorias, getIntermediarios, getUsers, log, quitarCaracteresEspeciales, saveCliente } from "../../utils";
import { Loader } from "../Loader";
import "./stylesClientesCargar.scss";

const handleContactSelectFocus = (e) => {
  [...e.target.options].forEach((opt) => {
    if (opt.value && opt.dataset.full) opt.textContent = opt.dataset.full;
  });
};
const handleContactSelectBlur = (e) => {
  [...e.target.options].forEach((opt) => {
    if (opt.value && opt.dataset.short) opt.textContent = opt.dataset.short;
  });
};

export const CargarClienteComponent = ({ onToggleVisibility, onSave }) => {
  const [categorias, setCategorias] = useState([]);
  const [intermediarios, setIntermediarios] = useState([]);
  const [showModalConfirmacion, setShowModalConfirmacion] = useState(false);
  const [temporalDataForm, setTemporalDataForm] = useState();
  const [usuarios, setUsuarios] = useState([]);
  const [isAuthLoaded, setIsAuthLoaded] = useState(false);
  const [isProductoSameAsEmpresa, setIsProductoSameAsEmpresa] = useState(false);
  const { pais, id } = useAuthContext();
  const paisMap = {
    Chile: "CHI",
    Uruguay: "URU",
    Perú: "PER",
  };
  const paisCode = paisMap[pais] || pais;
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setError,
    setValue,
    watch,
  } = useForm();
  useEffect(() => {
    if (pais !== undefined && id !== undefined && id !== null && id !== "0") {
      setIsAuthLoaded(true);
      if (onToggleVisibility) onToggleVisibility(true);
    } else {
      console.error("Error: ID de usuario no válido o no definido:", id);
      toast.error("No se pudo cargar el contexto de autenticación. Por favor, inicia sesión nuevamente.");
    }
    return () => {
      if (onToggleVisibility) onToggleVisibility(false);
    };
  }, [pais, id, onToggleVisibility]);
  const empresaValue = watch("empresa");
  useEffect(() => {
    if (isProductoSameAsEmpresa && empresaValue) {
      setValue("producto", empresaValue);
    }
  }, [isProductoSameAsEmpresa, empresaValue, setValue]);
  const onSubmit = useCallback(
    async (data) => {
      const hasTrailingSpacesEmpresa = /\s+$/.test(data.empresa);
      const hasTrailingSpacesProducto = /\s+$/.test(data.producto);
      if (hasTrailingSpacesEmpresa || hasTrailingSpacesProducto) {
        toast.warning("No se puede guardar: hay espacios al final en Empresa o Producto.");
        return;
      }
      if (!id || id === "0") {
        console.error("Error: ID de usuario no válido en onSubmit:", id);
        toast.error("Error: No se pudo obtener el ID del usuario autenticado.");
        return;
      }
      const userId = id.toString(); // Convertir id a string explícitamente
      const newCliente = {
        nombre: isProductoSameAsEmpresa
          ? quitarCaracteresEspeciales(data.empresa)
          : `${quitarCaracteresEspeciales(data.empresa)} | ${quitarCaracteresEspeciales(data.producto)}`,
        empresa: quitarCaracteresEspeciales(data.empresa),
        producto: quitarCaracteresEspeciales(data.producto),
        categoria_cliente: data.categoria_cliente,
        categoria: data.categoria,
        agencia: data.agencia,
        ejecutivo: userId === "904" ? "1" : userId === "306" ? "4" : userId,
        ejecutivos_asignados:
          userId === "904" || userId === "1"
            ? ["1", "904"]
            : userId === "4" || userId === "306"
              ? ["4", "306"]
              : [userId],
        contacto_cliente: data.contacto_cliente ? Number(data.contacto_cliente) : null,
        contacto_agencia: data.contacto_agencia ? Number(data.contacto_agencia) : null,
        pais,
        lastupdate: new Date().toISOString(),
      };
      setTemporalDataForm(newCliente);
      setShowModalConfirmacion(true);
    },
    [pais, id, isProductoSameAsEmpresa]
  );
  const saveButtonHandle = useCallback(async () => {
    try {
      setShowModalConfirmacion(false);
      await saveCliente(temporalDataForm);
      toast.success("El cliente se guardó correctamente");
      reset();
      setIsProductoSameAsEmpresa(false);
      if (onSave) onSave();
    } catch (error) {
      if (error.code === "23505") setError("empresa", { type: "custom", message: "El cliente ya existe" });
      console.error("Error en saveButtonHandle:", error);
      toast.error("Hubo un error al intentar guardar el nuevo cliente, prueba de nuevo.");
      log.error("saveButtonHandle", error);
    }
  }, [temporalDataForm, reset, setError, onSave]);
  const validarSelect = useCallback((value) => value !== "0", []);
  const disableSubmit = useMemo(() => showModalConfirmacion, [showModalConfirmacion]);
  const getFullLabel = useCallback(
    (user) => {
      const base = `${user.nombre} ${user.apellido || ""}`.trim();
      if (user.rol === "agencia" && intermediarios) {
        const inter = intermediarios.find((i) => String(i.id) === String(user.agencia));
        const name = inter ? (inter.nombre.includes("|") ? inter.nombre.split("|")[1].trim() : inter.nombre) : "";
        return name ? `${base}, ${name}` : base;
      }
      if (user.rol === "cliente" && user.email) return `${base}, ${user.email}`;
      return base;
    },
    [intermediarios]
  );
  useEffect(() => {
    if (pais !== undefined) {
      const initialize = async () => {
        const newIntermediarios = await getIntermediarios({ pais });
        setIntermediarios(newIntermediarios || []);
        const newCategorias = await getCategorias();
        setCategorias(newCategorias || []);
        const users = await getUsers({ pais });
        const uniqueUsers = [...new Map(users.map((user) => [user.id, user])).values()];
        const filteredUsuarios = uniqueUsers.filter(
          (user) => user.pais === paisCode && (user.rol === "cliente" || user.rol === "agencia")
        );
        filteredUsuarios.sort((a, b) => a.nombre.localeCompare(b.nombre));
        setUsuarios(filteredUsuarios);
      };
      initialize();
    }
  }, [pais, paisCode]);
  if (!isAuthLoaded) {
    return <Loader />;
  }
  if (intermediarios === undefined || categorias === undefined) {
    return <Loader />;
  }
  const validateLastChar = (value) => {
    const hasTrailingSpaces = /\s+$/.test(value);
    return !hasTrailingSpaces || "No se permiten espacios al final.";
  };
  return (
    <div className="form-container-cliente">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="empresa" className="form-label">
              Empresa
            </label>
            <input
              className={cx("form-input", errors?.empresa && "is-invalid")}
              id="empresa"
              placeholder="Ej.: LATAM AIRLINES, 25 caract. máx."
              type="text"
              maxLength={25}
              {...register("empresa", {
                required: "Este campo es obligatorio",
                validate: {
                  lastChar: (value) => validateLastChar(value),
                },
                onChange: (e) => {
                  e.target.value = e.target.value.toUpperCase();
                },
              })}
            />
            <div className="invalid-feedback">{errors?.empresa?.message}</div>
          </div>
          <div className="form-field">
            <div className="form-field-producto">
              <label htmlFor="producto" className="form-label">
                Producto
              </label>
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="sameAsEmpresa"
                  checked={isProductoSameAsEmpresa}
                  onChange={(e) => setIsProductoSameAsEmpresa(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="sameAsEmpresa">
                  Igual a Empresa
                </label>
              </div>
            </div>
            <input
              className={cx("form-input", errors?.producto && "is-invalid")}
              id="producto"
              placeholder="Ej.: LATAM AIRLINES, 25 caract. máx."
              type="text"
              maxLength={25}
              disabled={isProductoSameAsEmpresa}
              {...register("producto", {
                required: "Este campo es obligatorio",
                validate: {
                  lastChar: (value) => validateLastChar(value),
                },
                onChange: (e) => {
                  e.target.value = e.target.value.toUpperCase();
                },
              })}
            />
            <div className="invalid-feedback">{errors?.producto?.message}</div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="categoria_cliente" className="form-label">
              Tipo Cliente
            </label>
            <select
              className={cx("form-select", errors?.categoria_cliente && "is-invalid")}
              id="categoria_cliente"
              defaultValue="0"
              {...register("categoria_cliente", { validate: validarSelect })}
            >
              <option disabled value="0">
                Selecciona una opción
              </option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
            </select>
            <div className="invalid-feedback">Este campo es obligatorio</div>
          </div>
          <div className="form-field">
            <label htmlFor="categoria" className="form-label">
              Categoría
            </label>
            <select
              className={cx("form-select", errors?.categoria && "is-invalid")}
              id="categoria"
              defaultValue="0"
              {...register("categoria", { validate: validarSelect })}
            >
              <option disabled value="0">
                Selecciona una opción
              </option>
              {categorias.map(({ nombre, id }) => (
                <option key={id} value={id}>
                  {nombre}
                </option>
              ))}
            </select>
            <div className="invalid-feedback">Este campo es obligatorio</div>
          </div>
          <div className="form-field">
            <label htmlFor="agencia" className="form-label">
              Intermediario
            </label>
            <select
              className={cx("form-select", errors?.agencia && "is-invalid")}
              id="agencia"
              defaultValue="0"
              {...register("agencia", { validate: validarSelect })}
            >
              <option disabled value="0">
                Selecciona una opción
              </option>
              {intermediarios.map(({ nombre, id }) => (
                <option key={id} value={id}>
                  {nombre}
                </option>
              ))}
            </select>
            <div className="invalid-feedback">Este campo es obligatorio</div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="contacto_cliente" className="form-label">
              Contacto Cliente (opcional)
            </label>
            <select
              className="form-select"
              id="contacto_cliente"
              defaultValue=""
              {...register("contacto_cliente")}
              onFocus={handleContactSelectFocus}
              onBlur={handleContactSelectBlur}
            >
              <option value="">Seleccionar</option>
              {usuarios
                .filter((user) => user.rol === "cliente")
                .map((user) => {
                  const shortLabel = `${user.nombre} ${user.apellido || ""}`.trim();
                  return (
                    <option key={user.id} value={user.id} data-short={shortLabel} data-full={getFullLabel(user)}>
                      {shortLabel}
                    </option>
                  );
                })}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="contacto_agencia" className="form-label">
              Contacto Agencia (opcional)
            </label>
            <select
              className="form-select"
              id="contacto_agencia"
              defaultValue=""
              {...register("contacto_agencia")}
              onFocus={handleContactSelectFocus}
              onBlur={handleContactSelectBlur}
            >
              <option value="">Seleccionar</option>
              {usuarios
                .filter((user) => user.rol === "agencia")
                .map((user) => {
                  const shortLabel = `${user.nombre} ${user.apellido || ""}`.trim();
                  return (
                    <option key={user.id} value={user.id} data-short={shortLabel} data-full={getFullLabel(user)}>
                      {shortLabel}
                    </option>
                  );
                })}
            </select>
          </div>
        </div>
        <div className="form-row-button">
          <div className="text-center">
            <button
              className={cx("submit-button-guardar", disableSubmit && "disabled")}
              type="submit"
              disabled={disableSubmit}
            >
              {!disableSubmit ? (
                "CARGAR CLIENTE"
              ) : (
                <>
                  <Spinner size="sm" className="spinner">
                    GUARDANDO...
                  </Spinner>
                  <span>GUARDANDO</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
      <Modal isOpen={showModalConfirmacion} toggle={() => setShowModalConfirmacion(false)}>
        <ModalHeader>¿Está seguro?</ModalHeader>
        <ModalBody>Se está por guardar un nuevo cliente</ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowModalConfirmacion(false)}>
            Cancelar
          </Button>
          <Button color="primary" onClick={saveButtonHandle}>
            Guardar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};
